<script lang="ts">
/**
 * Page content panel that wraps Nuxt UI's `UDashboardPanel`.
 *
 * Automatically adjusts body padding when an `AppFooter` is present in the
 * parent `AppLayout`, so page content is never hidden behind fixed layout
 * elements. Forwards `header`, `body`, and `footer` slots to `UDashboardPanel`.
 *
 * @example
 * ```vue
 * <AppPagePanel id="items">
 *   <template #header>
 *     <UDashboardNavbar title="Items" />
 *   </template>
 *   <template #body>
 *     <!-- page content -->
 *   </template>
 * </AppPagePanel>
 * ```
 */
export interface AppPagePanelProps {
  /** Panel identifier used for state persistence and DOM id. */
  id?: string
}
</script>

<script setup lang="ts">
defineProps<AppPagePanelProps>()
</script>

<template>
  <UDashboardPanel :id="id">
    <template #header>
      <slot name="header" />
    </template>

    <template #body>
      <!--
        FAB clearance via CSS calc. The FAB sits at
        bottom-[calc(var(--app-footer-height)+1rem)] with size-14 (3.5rem).
        Total clearance = footer + 1rem gap + 3.5rem FAB + 1rem breathing room.
        Subtract UDashboardPanel's body p-4 (1rem) / sm:p-6 (1.5rem).
        On lg+, FAB is hidden so no extra clearance needed.
      -->
      <div
        class="flex flex-col gap-4 pb-[calc(var(--app-footer-height,0px)+4.5rem)] sm:gap-6 sm:pb-[calc(var(--app-footer-height,0px)+4rem)] lg:pb-0"
      >
        <slot name="body" />
      </div>
    </template>

    <template #footer>
      <slot name="footer" />
    </template>
  </UDashboardPanel>
</template>
